import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import { removeAdminUser, resetAdminUserTwoFactor, updateAdminUserRole } from '../../lib/api';
import { getHttpErrorMessage } from '../../lib/http';
import type { AdminRole, AdminUserRow } from '../../lib/types';

export function ManageAdminModal({
    admin,
    open,
    onClose,
    ownerCount,
    currentAdminId
}: {
    admin: AdminUserRow | null;
    open: boolean;
    onClose: () => void;
    ownerCount: number;
    currentAdminId: string;
}) {
    const [role, setRole] = useState<AdminRole>('support');
    const [ownerConfirmation, setOwnerConfirmation] = useState('');
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [removing, setRemoving] = useState(false);

    const qc = useQueryClient();
    const { toast } = useToast();

    useEffect(() => {
        if (!admin) return;
        setRole(admin.role);
        setOwnerConfirmation('');
    }, [admin]);

    if (!admin) return null;

    const busy = saving || resetting || removing;
    const isCurrentAdmin = admin.id === currentAdminId;
    const isSoleOwner = admin.role === 'owner' && ownerCount <= 1;
    const isOwnerRoleChange = admin.role === 'owner' && role !== admin.role;
    const hasValidOwnerConfirmation = ownerConfirmation.trim() === 'OWNER SAFE';

    const refresh = async () => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['admin', 'admin-audit', 'admins'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'admin-audit', 'kpis'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'admin-audit', 'audit'] })
        ]);
    };

    const handleSave = async () => {
        if (role === admin.role) {
            onClose();
            return;
        }
        if (isCurrentAdmin && isSoleOwner && role !== 'owner') {
            toast('Cannot demote the only owner', {
                kind: 'danger',
                detail: 'Promote another owner first, then modify this admin.'
            });
            return;
        }
        if (isOwnerRoleChange && !hasValidOwnerConfirmation) {
            toast('Owner-safe confirmation required', {
                kind: 'danger',
                detail: 'Enter OWNER SAFE to downgrade this owner role.'
            });
            return;
        }

        setSaving(true);
        try {
            await updateAdminUserRole(admin.id, role);
            await refresh();
            toast('Admin role updated', { detail: `${admin.email} · ${role}` });
            onClose();
        } catch (error) {
            toast('Failed to update admin role', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Role could not be updated')
            });
        } finally {
            setSaving(false);
        }
    };

    const handleResetTwoFactor = async () => {
        setResetting(true);
        try {
            const result = await resetAdminUserTwoFactor(admin.id);
            await refresh();
            toast('Admin 2FA reset', {
                detail:
                    result.removed > 0
                        ? `${admin.email} · removed ${result.removed} factor${result.removed === 1 ? '' : 's'}`
                        : `${admin.email} · no enrolled factors were present`
            });
            onClose();
        } catch (error) {
            toast('Failed to reset admin 2FA', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, '2FA could not be reset')
            });
        } finally {
            setResetting(false);
        }
    };

    const handleRemove = async () => {
        if (admin.role === 'owner' && ownerCount <= 1) {
            toast('Cannot remove the only owner', {
                kind: 'danger',
                detail: 'Promote another owner first, then remove this owner.'
            });
            return;
        }
        if (admin.role === 'owner' && !hasValidOwnerConfirmation) {
            toast('Owner-safe confirmation required', {
                kind: 'danger',
                detail: 'Enter OWNER SAFE to remove this owner.'
            });
            return;
        }
        const confirmed = window.confirm(`Remove admin access for ${admin.email}?`);
        if (!confirmed) return;

        setRemoving(true);
        try {
            await removeAdminUser(admin.id);
            await refresh();
            toast('Admin access removed', { detail: admin.email });
            onClose();
        } catch (error) {
            toast('Failed to remove admin', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Admin access could not be removed')
            });
        } finally {
            setRemoving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={() => {
                if (!busy) onClose();
            }}
            title="Manage admin"
            subtitle="Update role metadata, reset 2FA, or revoke admin access."
            width={520}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={busy}
                    >
                        {saving ? 'Saving…' : 'Save role'}
                    </RoleButton>
                </>
            }
        >
            <div className="form-field">
                <span className="form-label">Admin</span>
                <div
                    style={{
                        display: 'grid',
                        gap: 4,
                        padding: '10px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        background: 'color-mix(in oklab, var(--surface-elev-2) 75%, transparent)'
                    }}
                >
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                        {admin.name}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{admin.email}</span>
                </div>
            </div>

            <div className="form-field">
                <span className="form-label">Role</span>
                {admin.role === 'owner' ? (
                    <div
                        style={{
                            display: 'grid',
                            gap: 4,
                            padding: 12,
                            border: '1px solid color-mix(in oklab, var(--warning) 28%, var(--border))',
                            borderRadius: 8,
                            background:
                                'color-mix(in oklab, var(--warning-surface) 70%, transparent)'
                        }}
                    >
                        <span
                            style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}
                        >
                            Owner-safe action
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            Enter <strong>OWNER SAFE</strong> to confirm any owner role change or
                            owner removal.
                        </span>
                        <input
                            className="form-input"
                            value={ownerConfirmation}
                            onChange={(e) => setOwnerConfirmation(e.target.value)}
                            placeholder="OWNER SAFE"
                        />
                    </div>
                ) : null}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <RoleOption
                        id="owner"
                        role={role}
                        setRole={setRole}
                        disabled={isCurrentAdmin && isSoleOwner && role !== 'owner'}
                        title="Owner"
                        description="Admin management, destructive actions, and policy changes."
                    />
                    <RoleOption
                        id="support"
                        role={role}
                        setRole={setRole}
                        disabled={isCurrentAdmin && isSoleOwner}
                        title="Support"
                        description="Tickets and operational workflows without owner-only actions."
                    />
                    <RoleOption
                        id="read_only"
                        role={role}
                        setRole={setRole}
                        disabled={isCurrentAdmin && isSoleOwner}
                        title="Read-only"
                        description="Can inspect admin pages but cannot run actions."
                    />
                </div>
            </div>

            <div
                style={{
                    display: 'grid',
                    gap: 10,
                    padding: 12,
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    background: 'color-mix(in oklab, var(--surface-elev-2) 70%, transparent)'
                }}
            >
                <div style={{ display: 'grid', gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Security
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {admin.tfa_enabled
                            ? 'Verified MFA factors are enrolled for this admin.'
                            : 'No verified MFA factors were detected for this admin.'}
                    </span>
                </div>
                <RoleButton
                    scope="owner_only"
                    className="btn btn-ghost"
                    onClick={handleResetTwoFactor}
                    disabled={busy}
                >
                    {resetting ? 'Resetting 2FA…' : 'Reset 2FA'}
                </RoleButton>
            </div>

            <div
                style={{
                    display: 'grid',
                    gap: 10,
                    marginTop: 16,
                    padding: 12,
                    border: '1px solid color-mix(in oklab, var(--danger) 28%, var(--border))',
                    borderRadius: 10,
                    background: 'color-mix(in oklab, var(--danger-surface) 75%, transparent)'
                }}
            >
                <div style={{ display: 'grid', gap: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Remove admin access
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Revokes access from the existing users table while preserving the audit
                        trail.
                    </span>
                </div>
                <RoleButton
                    scope="owner_only"
                    className="btn btn-danger"
                    onClick={handleRemove}
                    disabled={busy || (admin.role === 'owner' && ownerCount <= 1)}
                >
                    {removing ? 'Removing…' : 'Remove admin'}
                </RoleButton>
            </div>
        </Modal>
    );
}

function RoleOption({
    id,
    role,
    setRole,
    disabled,
    title,
    description
}: {
    id: AdminRole;
    role: AdminRole;
    setRole: (role: AdminRole) => void;
    disabled?: boolean;
    title: string;
    description: string;
}) {
    const checked = role === id;
    const interactive = !disabled;
    return (
        <label
            style={{
                display: 'grid',
                gridTemplateColumns: '18px 1fr',
                gap: 10,
                padding: '10px 12px',
                border: `1px solid ${checked ? 'var(--border-focus)' : 'var(--border)'}`,
                borderRadius: 8,
                cursor: interactive ? 'pointer' : 'not-allowed',
                background: checked ? 'var(--primary-surface)' : 'transparent'
            }}
            aria-disabled={!interactive}
        >
            <input
                type="radio"
                name="manage-role"
                checked={checked}
                disabled={!interactive}
                onChange={() => setRole(id)}
                style={{ marginTop: 2 }}
            />
            <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {description}
                </div>
            </div>
        </label>
    );
}
